/**
 * Turns a react-router navigation into a low-cardinality route pattern.
 *
 * Why a pattern rather than the pathname: `/project/1234/builders/9` is a
 * distinct string for every project, so grouping by it produces one row per URL
 * and answers nothing. `/project/:p_id/builders/:cb_id` groups.
 *
 * Two things make this harder than joining strings.
 *
 * **Absolute child paths.** Routes are declared with a mix of relative segments
 * (`:h_id/settings` under a `hubs` parent) and absolute ones (`/browse/*`,
 * `/accept/hub-invitation`) nested under pathless layout routes. A naive join
 * yields `//browse/*`, so each segment is normalised.
 *
 * **Modals outside the route tree.** `HubModals` matches the location with
 * `matchPath` and renders over whichever page already matched, so
 * `/hubs/7/members/42/edit` resolves in `state.matches` to
 * `/hubs/:h_id/members/*` — the `:u_id` exists only in the pathname. Those
 * patterns are re-matched here.
 *
 * Note we never use `match.route.id`: with `createRoutesFromElements` and no
 * explicit ids, it is a positional tree path that shifts whenever a `<Route>` is
 * inserted above it. Useless for anything persisted.
 */

import { matchPath } from "react-router";

/**
 * The hub-modal patterns, most specific first.
 *
 * Duplicated from `HUB_MODAL_PATHS` in RouterComponent/components/RebelRouter
 * rather than imported, deliberately. Importing it created a cycle — the app
 * chrome pulls in the logout action, which pulls in this module, which pulled in
 * RebelRouter — and a cycle here does not fail loudly: it leaves the list
 * `undefined` at module-eval time and takes out every route that renders the
 * sidebar.
 *
 * The copy is kept honest by a test: routePattern.test.ts asserts this array
 * equals the one RebelRouter exports, so drift is a failing test rather than a
 * silently coarser route pattern. Tests are not in the app's module graph, so
 * they can import both sides safely.
 */
const HUB_MODAL_PATHS = [
  "/hubs/:h_id/members/:u_id/edit",
  "/hubs/:h_id/members/:u_id",
  "/hubs/:h_id/welcome",
];

interface RouteLike {
  route?: { path?: string };
  params?: Record<string, string | undefined>;
}

export interface ResolvedRoute {
  route: string;
  params: Record<string, string>;
}

/** Exported for the parity test against RebelRouter's list. */
export const hubModalPatterns = (): string[] => [...HUB_MODAL_PATHS];

const stripSlashes = (segment: string): string => segment.replace(/^\/+|\/+$/g, "");

const definedParams = (params: Record<string, string | undefined> = {}): Record<string, string> =>
  Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
    // react-router puts the splat remainder under "*". It is a raw path
    // fragment, not an identifier, and can carry anything — leave it out.
    if (value !== undefined && key !== "*") acc[key] = value;
    return acc;
  }, {});

/**
 * Joins the declared paths of a match chain into one pattern.
 * Pathless layout routes contribute nothing.
 */
export const joinRoutePattern = (matches: RouteLike[]): string => {
  const segments = (matches ?? [])
    .map((match) => match?.route?.path)
    .filter((path): path is string => Boolean(path))
    .map(stripSlashes)
    .filter(Boolean);

  return `/${segments.join("/")}`;
};

/**
 * The pattern and params for a navigation, preferring a hub-modal pattern when
 * the route tree only produced a splat.
 */
export const resolveRoutePattern = (matches: RouteLike[], pathname: string): ResolvedRoute => {
  const joined = joinRoutePattern(matches);
  const lastMatch = (matches ?? []).at(-1);
  const fromTree: ResolvedRoute = { route: joined, params: definedParams(lastMatch?.params) };

  // Only worth re-matching when the tree gave up specificity. A concrete pattern
  // is always the better answer.
  if (!joined.endsWith("/*")) return fromTree;

  for (const path of HUB_MODAL_PATHS) {
    const match = matchPath(path, pathname);
    if (match) return { route: path, params: definedParams(match.params) };
  }

  return fromTree;
};
